import {
  BellIcon,
  ChatIcon,
  CheckIcon,
  EyeIcon,
  HandshakeIcon,
  ListIcon,
  LockIcon,
  ReceiptIcon,
  SendIcon,
  ShieldIcon,
  SparkIcon,
} from '@/components/icons';
import {
  delegate,
  finalCta,
  hero,
  objection,
  pricing,
  problem,
  productLabel,
  socialProof,
  value,
  workflow,
} from '@/config/collectionsLanding';

interface CtaProps {
  onCtaClick: () => void;
}

function PrimaryButton({
  children,
  onClick,
  className = '',
}: {
  children: React.ReactNode;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-xl bg-brand-600 px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-brand-600/20 transition hover:bg-brand-700 active:scale-[0.99] ${className}`}
    >
      {children}
    </button>
  );
}

export function SiteHeader() {
  return (
    <header className="border-b border-slate-100 bg-white/80 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
        <div className="flex items-center gap-2">
          <img
            src="/images/icon_192x192.png"
            alt=""
            aria-hidden="true"
            className="h-10 w-10 rounded-lg object-contain"
          />
          <span className="text-lg font-extrabold tracking-tight text-ink-900">
            {productLabel}
          </span>
        </div>
        <span className="hidden text-sm font-medium text-ink-500 sm:block">
          {hero.eyebrow}
        </span>
      </div>
    </header>
  );
}

export function Hero({ onCtaClick }: CtaProps) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-slate-50">
      <div className="mx-auto grid max-w-6xl gap-12 px-5 py-14 sm:py-20 lg:grid-cols-2 lg:items-center">
        <div className="animate-fade-in-up">
          <span className="inline-block rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand-700">
            {hero.eyebrow}
          </span>
          <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight text-ink-900 sm:text-5xl">
            {hero.headline}
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-ink-600">
            {hero.subheadline}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <PrimaryButton onClick={onCtaClick}>
              {hero.primaryCta}
              <SendIcon width={18} height={18} />
            </PrimaryButton>
          </div>
          <p className="mt-4 flex items-center gap-2 text-sm font-medium text-ink-500">
            <LockIcon width={16} height={16} className="text-emerald-600" />
            {hero.secondaryTrust}
          </p>
        </div>

        <WorkflowIllustration />
      </div>
    </section>
  );
}

/** Visual workflow built from UI elements/icons - not stock imagery. */
function WorkflowIllustration() {
  return (
    <div className="relative animate-fade-in-up" aria-hidden="true">
      <div className="mx-auto max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-200/60">
        <div className="flex items-center justify-between">
          <span className="text-sm font-bold text-ink-800">בקשת תשלום #1042</span>
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
            ממתין לתשלום
          </span>
        </div>
        <div className="mt-4 space-y-3">
          {[
            { icon: <SendIcon width={16} height={16} />, text: 'נשלח בוואטסאפ · 09:14', tone: 'text-brand-600' },
            { icon: <EyeIcon width={16} height={16} />, text: 'נצפה על ידי הלקוח · 09:20', tone: 'text-ink-500' },
            { icon: <BellIcon width={16} height={16} />, text: 'תזכורת מתוזמנת למחר', tone: 'text-ink-500' },
          ].map((row) => (
            <div key={row.text} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5">
              <span className={row.tone}>{row.icon}</span>
              <span className="text-sm text-ink-700">{row.text}</span>
            </div>
          ))}
          <div className="flex items-center gap-3 rounded-lg bg-emerald-50 px-3 py-2.5">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-emerald-600 text-white">
              <CheckIcon width={14} height={14} />
            </span>
            <span className="text-sm font-semibold text-emerald-800">שולם · נשלחה קבלה</span>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-3 text-sm">
          <span className="text-ink-500">צפוי להיכנס החודש</span>
          <span className="font-bold text-ink-900">₪12,480</span>
        </div>
      </div>
    </div>
  );
}

const delegateIcons = [SparkIcon, SendIcon, ShieldIcon];

export function DelegateSection({ onCtaClick }: CtaProps) {
  return (
    <section className="bg-white py-16">
      <div className="mx-auto max-w-5xl px-5">
        <h2 className="text-center text-3xl font-extrabold tracking-tight text-ink-900">
          {delegate.title}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-lg leading-relaxed text-ink-600">
          {delegate.subtitle}
        </p>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {delegate.cards.map((card, i) => {
            const Icon = delegateIcons[i % delegateIcons.length];
            return (
              <div
                key={card.title}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-6 shadow-sm"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-50 text-brand-600">
                  <Icon width={22} height={22} />
                </span>
                <h3 className="mt-4 text-base font-bold text-ink-900">{card.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">{card.body}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-10 text-center">
          <PrimaryButton onClick={onCtaClick}>{hero.primaryCta}</PrimaryButton>
        </div>
      </div>
    </section>
  );
}

const problemIcons = [ListIcon, BellIcon, HandshakeIcon, ReceiptIcon];

export function ProblemSection() {
  return (
    <section className="mx-auto max-w-6xl px-5 py-16">
      <h2 className="text-center text-3xl font-extrabold tracking-tight text-ink-900">
        {problem.title}
      </h2>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {problem.cards.map((card, i) => {
          const Icon = problemIcons[i % problemIcons.length];
          return (
            <div
              key={card}
              className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-brand-50 text-brand-600">
                <Icon width={22} height={22} />
              </span>
              <p className="mt-4 text-base font-medium leading-relaxed text-ink-700">{card}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function WorkflowSection() {
  return (
    <section className="bg-white py-16">
      <div className="mx-auto max-w-6xl px-5">
        <h2 className="text-center text-3xl font-extrabold tracking-tight text-ink-900">
          {workflow.title}
        </h2>
        <ol className="mt-10 grid gap-4 md:grid-cols-5">
          {workflow.steps.map((step, i) => (
            <li key={step} className="relative flex flex-col items-center text-center">
              <span className="grid h-12 w-12 place-items-center rounded-full bg-brand-600 text-lg font-bold text-white">
                {i + 1}
              </span>
              <p className="mt-3 text-sm font-medium leading-relaxed text-ink-700">{step}</p>
              {i < workflow.steps.length - 1 && (
                <span className="absolute top-6 hidden h-px w-full bg-slate-200 ltr:left-1/2 rtl:right-1/2 md:block" />
              )}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

const valueIcons = [SparkIcon, HandshakeIcon, EyeIcon, ShieldIcon];

export function ValueSection() {
  return (
    <section className="mx-auto max-w-6xl px-5 py-16">
      <h2 className="text-center text-3xl font-extrabold tracking-tight text-ink-900">
        {value.title}
      </h2>
      <div className="mx-auto mt-10 grid max-w-3xl gap-4">
        {value.bullets.map((bullet, i) => {
          const Icon = valueIcons[i % valueIcons.length];
          return (
            <div
              key={bullet}
              className="flex items-center gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                <Icon width={20} height={20} />
              </span>
              <p className="text-base font-medium text-ink-700">{bullet}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function ObjectionSection() {
  return (
    <section className="bg-white py-16">
      <div className="mx-auto max-w-5xl px-5">
        <h2 className="text-center text-3xl font-extrabold tracking-tight text-ink-900">
          {objection.title}
        </h2>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {objection.cards.map((card) => (
            <div
              key={card.title}
              className="rounded-2xl border border-slate-100 bg-slate-50 p-6 shadow-sm"
            >
              <h3 className="text-base font-bold text-ink-900">{card.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-ink-600">{card.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SocialProofSection() {
  if (!socialProof.show || socialProof.count <= 0) return null;
  return (
    <section className="bg-white pb-4 pt-12">
      <div className="mx-auto max-w-3xl px-5 text-center">
        <p className="text-lg font-bold text-ink-900">
          {socialProof.template.replace('{count}', String(socialProof.count))}
        </p>
        <p className="mt-2 text-sm text-ink-500">{socialProof.caption}</p>
      </div>
    </section>
  );
}

export function PricingSection({ onCtaClick }: CtaProps) {
  return (
    <section className="bg-gradient-to-b from-slate-50 to-white py-16">
      <div className="mx-auto max-w-3xl px-5 text-center">
        <div className="rounded-3xl border border-brand-100 bg-white p-10 shadow-lg shadow-brand-600/5">
          <h2 className="text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl">
            {pricing.title}
          </h2>
          <p className="mt-4 text-lg text-ink-600">{pricing.text}</p>
          <div className="mt-8">
            <PrimaryButton onClick={onCtaClick}>{finalCta.cta}</PrimaryButton>
          </div>
        </div>
      </div>
    </section>
  );
}

export function FinalCta({ onCtaClick }: CtaProps) {
  return (
    <section className="bg-brand-700 py-16">
      <div className="mx-auto max-w-3xl px-5 text-center">
        <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-white">
          {finalCta.headline}
        </h2>
        <div className="mt-8">
          <PrimaryButton
            onClick={onCtaClick}
            className="!bg-white !text-brand-700 !shadow-white/20 hover:!bg-brand-50"
          >
            {finalCta.cta}
            <ChatIcon width={18} height={18} />
          </PrimaryButton>
        </div>
      </div>
    </section>
  );
}

export function SiteFooter() {
  return (
    <footer className="border-t border-slate-100 bg-white py-8">
      <div className="mx-auto flex max-w-6xl flex-col items-center gap-2 px-5 text-center text-sm text-ink-500">
        <span className="font-semibold text-ink-700">{productLabel}</span>
        <span>{hero.secondaryTrust}</span>
        <span className="text-xs">© {new Date().getFullYear()} Clipiti</span>
      </div>
    </footer>
  );
}
